import autoload from "./autoload";
import Axios from "axios";
import auth from "./mixins/auth";
import cart from "./mixins/cart";
import initDom from "./mixins/init_dom";
import colorFilter from "./mixins/color_filter";
import MX_applicator from "./mixins/applicator";
import HelpersMixin from "./mixins/helpers";

autoload();

Vue.component(
  "applicator-map",
  require("./components/applicators/ApplicatorMap.vue").default
);
Vue.component(
  "applicator-card",
  require("./components/applicators/ApplicatorCard.vue").default
);
Vue.component(
  "applicator-details",
  require("./components/applicators/ApplicatorDetails.vue").default
);
Vue.component(
  "applicator-map-marker",
  require("./components/applicators/ApplicatorMapMarker.vue").default
);
Vue.component(
  "applicator-map-search",
  require("./components/applicators/ApplicatorMapSearch.vue").default
);
Vue.component(
  "applicator-map-filters",
  require("./components/applicators/ApplicatorMapFilters.vue").default
);

Vue.component("spinner", require("./components/Spinner.vue").default);

window.app = new Vue({
  el: "#vue-applicator-map",

  mixins: [initDom, colorFilter, auth, cart, MX_applicator, HelpersMixin],

  data() {
    return {
      map: undefined,

      applicators: [],
      selectedApplicator: undefined,
      lastScrollPosition: 0,

      mapState: undefined,
      loadingApplicator: false,
      loadingApplicators: false,
      sidebarLocation: "left",
      filtersVisibleOnMobile: false,
      filtersVisibleOnDesktop: false,
      hasSearchQuery: false,
      expandedResults: false,
      showExpandedResults: false,

      defaults: {
        lat: 42.3265,
        lng: -122.8756,
        radius: 40,
      },

      howToTitle: "Tips for choosing a Certified Applicator",
      howToContent: `<p>Each applicator is an independent business and in some cases specialize in unique types of Cerakote work. Here's some tips on how to find the best local applicator for your project!</p>
        <ul>
          <li>Gallery - View the quality of their work and what they specialize in</li>
          <li>Website - A good place to get more specific information</li>
          <li>Bio - Their short summary about their business</li>
          <li>Testimonials - Review their customer testimonials</li>
        </ul>
        <br />
        Manufacturers, please contact Cerakote directly for assistance.`,
    };
  },

  computed: {
    TotalApplicators() {
      return Object.keys(this.applicators).length;
    },

    SearchLatitude() {
      return this.mapState ? this.mapState.lat : this.defaults.lat;
    },

    SearchLongitude() {
      return this.mapState ? this.mapState.lng : this.defaults.lng;
    },

    SearchRadius() {
      return this.mapState ? this.mapState.radius : this.defaults.radius;
    },

    /**
     * @method SearchParams
     * @created September 4th, 2020
     * @author Sebastian Inman <sinman@nicindustries.com>
     * @description Build the query parameter search query for the applicator search.
     * @returns String
     */

    SearchParams() {
      let params = {
        lat: this.SearchLatitude,
        lon: this.SearchLongitude,
        radius: Math.round(this.SearchRadius),
        domain: "cerakote",
      };

      // Convert the object to a URL param string.
      return Object.keys(params)
        .map((key) => `${key}=${params[key]}`)
        .join("&");
    },

    /**
     * @method SearchURL
     * @created September 4th, 2020
     * @author Sebastian Inman <sinman@nicindustries.com>
     * @description Builds the full API endpoint using the generated search parameters.
     * @returns String
     */

    SearchURL() {
      return `/api/proxify/applicators?${this.SearchParams}`;
    },
  },

  mounted() {
    this.map = this.$refs["applicator-map"];
  },

  methods: {
    async UpdateMapState({ state, filters }) {
      this.mapState = state;
      this.hasSearchQuery = true;
      await this.FetchApplicators(filters);
    },

    /**
     * @method FetchApplicators
     * @created September 4th, 2020
     * @author Sebastian Inman <sinman@nicindustries.com>
     * @description Sends a request to the API to fetch all applicators
     * within the current search radius of the map.
     * @returns void
     */

    async FetchApplicators(filters = {}) {
      if (!this.loadingApplicators) {
        this.loadingApplicators = true;
        this.expandedResults = false;
        this.showExpandedResults = false;
        this.applicators = [];

        this.scrollToPosition(0, 0);

        const { data: response } = await Axios.get(this.SearchURL);

        if(response.outside_of_radius === true) {
          this.expandedResults = true;
        } else {
          this.expandedResults = false;
        }

        await Promise.all(
          response.profiles.map((applicator) => this.applicators.push(applicator))
        );

        // Initial Sort for firefox
        this.applicators.sort((a, b) =>
          b.review_count - a.review_count ? 1 : -1
        );

        this.FilterApplicators(filters);

        // Customer's choice logic. Identifies user with highest customer score, returns their index, and then updates the user with the customer's choice rating.
        if (this.applicators.length > 0) {
          // First we filter the initial results to exclude distributors from 'customer's choice' picks.
          let customerChoice = this.applicators.filter(function (obj) {
            if (
              !obj.badges.includes("distributor") &&
              !obj.badges.includes("cerakote_distributor")
            ) {
              return obj;
            }
          });

          // If there are still results after we filter out distributors, we then find the user with the highest customer score.
          if (customerChoice.length > 0) {
            customerChoice = customerChoice.reduce(function (prev, current) {
              if (+current.review_count > +prev.review_count) {
                return current;
              } else {
                return prev;
              }
            });

            // We find the index value in the applicators results matching this highest number user, and then set their 'is customer choice' flag.
            let objIndex = this.applicators.findIndex(
              (obj) => obj.user_id == customerChoice.user_id
            );

            if (this.applicators[objIndex].rating > 0) {
              this.applicators[objIndex].is_customer_choice = true;
            }
          }
        }

        // Here we sort all distributors to the top of the returned results.
        this.applicators.sort((a, b) =>
          a.badges.includes("distributor") ||
          a.badges.includes("cerakote_distributor")
            ? -1
            : 1
        );

        this.loadingApplicators = false;
      }
    },

    async SelectApplicator(applicator) {
      this.loadingApplicator = applicator.user_id;

      await this.LoadExtraApplicatorDetails(applicator).then(async (data) => {
        applicator = { ...applicator, ...data };
      });

      if (applicator.review_count > 0) {
        await this.LoadApplicatorReviews(applicator).then((reviews) => {
          applicator = { ...applicator, ...{ loadedReviews: reviews } };
        });
      }

      this.lastScrollPosition =
        document.documentElement.scrollTop || document.body.scrollTop;
      this.scrollToPosition(0, 0);

      this.selectedApplicator = applicator;
      this.loadingApplicator = false;
    },

    async LoadExtraApplicatorDetails(applicator) {
      let params = {
        fields: [
          "about",
          "banner_picture_url",
          "company_phone",
          "facebook_url",
          "instagram_url",
          "twitter",
          "website",
        ].join(","),
      };

      return new Promise(async (resolve) => {
        let query = Object.keys(params)
          .map((key) => `${key}=${params[key]}`)
          .join("&");
        let { data } = await axios.get(
          `/api/proxify/applicators/by-user-id/${applicator.user_id}?domain=cerakote`
        );
        resolve(data);
      });
    },

    async LoadApplicatorReviews(applicator, limit = 10) {
      return new Promise(async (resolve) => {
        let { data } = await axios.get(
          `/api/v2/proxify/applicators/${applicator.user_id}/reviews?limit=${limit}&domain=cerakote`
        );
        resolve(data);
      });
    },

    DeselectApplicator() {
      this.selectedApplicator = undefined;

      this.scrollToPosition(this.lastScrollPosition, 0);
      this.lastScrollPosition = 0;
    },

    openHowToModal() {
      $("#informationModal").modal("show");
    },

    ToggleFiltersVisibilityOnDesktop() {
      this.filtersVisibleOnDesktop = !this.filtersVisibleOnDesktop;
    },

    ShowExpandedApplicators() {
      this.showExpandedResults = !this.showExpandedResults;
    },


    ToggleFiltersVisibilityOnMobile() {
      this.filtersVisibleOnMobile = !this.filtersVisibleOnMobile;
      this.$root.showFooter = !this.$root.showFooter;
    },

    FilterApplicators(filters) {
      /**
       * First check to see if the MultiCam filter is applied.
       * If it's applied, filter out any applicators that are NOT licensed by MultiCam.
       */

      if (filters.multiCam) {
        this.applicators = this.applicators.filter((applicator) =>
          applicator.badges.includes("cerakote_multicam")
        );
      }

      /**
       * Next we check if any business type filters are applied.
       * If there's at least one business type filter applied, we loop through each
       * of the applied business type filters and check to see if each applicator
       * has that business type assigned to their profile. We then filter out any
       * applicator that DOES NOT have any of the business type filters applied.
       */

      if (filters.businessTypes && filters.businessTypes.length) {
        this.applicators = this.applicators.filter((applicator) => {
          let businessTypeFiltersApplied = [];

          Object.values(filters.businessTypes).map((filter) => {
            if (
              applicator.business_types !== null &&
              applicator.business_types.filter(
                (businessType) => businessType === filter
              ).length > 0
            ) {
              businessTypeFiltersApplied.push(applicator);
            }
          });

          return businessTypeFiltersApplied.length > 0;
        });
      }

      /**
       * Next we check if any work accepted filters are applied.
       * If there's at least one work accepted filter applied, we loop through each
       * of the applied work accepted filters and check to see if each applicator
       * has that work accepted assigned to their profile. We then filter out any
       * applicator that DOES NOT have any of the work accepted filters applied.
       */

      if (filters.workAccepted && filters.workAccepted.length) {
        this.applicators = this.applicators.filter((applicator) => {
          let workAcceptedFiltersApplied = [];

          Object.values(filters.workAccepted).map((filter) => {
            if (
              applicator.work_accepted !== null &&
              applicator.work_accepted.filter(
                (workAccepted) => workAccepted === filter
              ).length > 0
            ) {
              workAcceptedFiltersApplied.push(applicator);
            }
          });

          return workAcceptedFiltersApplied.length > 0;
        });
      }

      /**
       * Distributor filtering.
       */

      if (filters.distributors && filters.distributors.length) {
        this.applicators = this.applicators.filter((applicator) => {
          let distributorFiltersApplied = [];

          Object.values(filters.distributors).map((filter) => {
            if (applicator.badges.includes(filter)) {
              distributorFiltersApplied.push(applicator);
            }
          });

          return distributorFiltersApplied.length > 0;
        });
      }

      /**
       * Finally we check if any certification filters are applied.
       * If there's at least one work accepted filter applied, we loop through each
       * of the applied work accepted filters and check to see if each applicator
       * has that work accepted assigned to their profile. We then filter out any
       * applicator that DOES NOT have any of the work accepted filters applied.
       */

      if (filters.certifications && filters.certifications.length) {
        this.applicators = this.applicators.filter((applicator) => {
          let certificationFiltersApplied = [];

          Object.values(filters.certifications).map((filter) => {
            if (applicator.badges.includes(filter)) {
              certificationFiltersApplied.push(applicator);
            }
          });

          return certificationFiltersApplied.length > 0;
        });
      }

      /**
       * Finally we check if any certification filters are applied.
       * If there's at least one work accepted filter applied, we loop through each
       * of the applied work accepted filters and check to see if each applicator
       * has that work accepted assigned to their profile. We then filter out any
       * applicator that DOES NOT have any of the work accepted filters applied.
       */

      if (filters.rating && filters.rating.length) {
        this.applicators = this.applicators.filter((applicator) => {
          let ratingFiltersApplied = [];

          Object.values(filters.rating).map((filter) => {
            if (applicator.cerakote_rating === filter) {
              ratingFiltersApplied.push(applicator);
            }
          });

          return ratingFiltersApplied.length > 0;
        });
      }
    },

    SortApplicators() {},
  },
});
